/**
 * Mock org-member directory used by the Stream A fetch interceptor.
 *
 * Shape mirrors org-service's OrgMemberResponseDto / the auth-service
 * ShellOrgMemberController response so the Angular frontend can be
 * developed against realistic data before real Kong routing is available.
 *
 * `displayName` is a supplementary field the mock returns; it is not
 * present in the real OrgMemberView Java class — the real API will
 * return it once user-enrichment is wired (Phase 5).
 */

export type OrgMemberStatus = 'ACTIVE' | 'INVITED' | 'DEACTIVATED';

export interface MockOrgMember {
  readonly id: string;
  readonly orgId: string;
  readonly userId: string | null;
  readonly email: string;
  readonly role: string;
  readonly status: OrgMemberStatus;
  readonly displayName: string | null;
  readonly joinedAt: string | null;
  readonly invitationSentAt: string | null;
  readonly invitationTokenExpiresAt: string | null;
  readonly invitationAcceptedAt: string | null;
  readonly invitedBy: string | null;
  readonly createdAt: string;
  readonly isActive: boolean;
}

/**
 * Seed data — 'acme' org, matching the personas in users.ts so mock-api
 * routes them to the right tenant.
 */
export const SEED_ORG_MEMBERS: readonly MockOrgMember[] = [
  {
    id:                         'om-acme-001',
    orgId:                      'acme',
    userId:                     '01HZP3CB6MKQ9X2TYACME001',
    email:                      'owner@acme.test',
    role:                       'Admin',
    status:                     'ACTIVE',
    displayName:                'Acme Owner',
    joinedAt:                   '2024-01-15T10:00:00-05:00',
    invitationSentAt:           null,
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       null,
    invitedBy:                  null,
    createdAt:                  '2024-01-15T10:00:00-05:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-002',
    orgId:                      'acme',
    userId:                     '01HZP3CB6MKQ9X2TYACME002',
    email:                      'auditor@acme.test',
    role:                       'Read only',
    status:                     'ACTIVE',
    displayName:                'Acme Auditor',
    joinedAt:                   '2024-03-02T10:00:00-05:00',
    invitationSentAt:           '2024-02-28T10:00:00-05:00',
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       '2024-03-02T10:00:00-05:00',
    invitedBy:                  '01HZP3CB6MKQ9X2TYACME001',
    createdAt:                  '2024-02-28T10:00:00-05:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-003',
    orgId:                      'acme',
    userId:                     '01HZP3CB6MKQ9X2TYACME003',
    email:                      'robin.patel@acme.test',
    role:                       'Department Head',
    status:                     'ACTIVE',
    displayName:                'Robin Patel',
    joinedAt:                   '2024-06-12T10:00:00-05:00',
    invitationSentAt:           '2024-06-10T10:00:00-05:00',
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       '2024-06-12T10:00:00-05:00',
    invitedBy:                  '01HZP3CB6MKQ9X2TYACME001',
    createdAt:                  '2024-06-10T10:00:00-05:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-004',
    orgId:                      'acme',
    userId:                     null,
    email:                      'casey.t@acme.test',
    role:                       'Member',
    status:                     'INVITED',
    displayName:                'Casey Tanaka',
    joinedAt:                   null,
    invitationSentAt:           '2026-04-26T09:30:00-05:00',
    invitationTokenExpiresAt:   '2026-05-03T09:30:00-05:00',
    invitationAcceptedAt:       null,
    invitedBy:                  '01HZP3CB6MKQ9X2TYACME001',
    createdAt:                  '2026-04-26T09:30:00-05:00',
    isActive:                   false,
  },
  {
    id:                         'om-acme-005',
    orgId:                      'acme',
    userId:                     '01HZP3CB6MKQ9X2TYACME005',
    email:                      'jordan.l@acme.test',
    role:                       'Member',
    status:                     'ACTIVE',
    displayName:                'Jordan Lee',
    joinedAt:                   '2025-02-10T10:00:00-05:00',
    invitationSentAt:           '2025-02-08T10:00:00-05:00',
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       '2025-02-10T10:00:00-05:00',
    invitedBy:                  '01HZP3CB6MKQ9X2TYACME001',
    createdAt:                  '2025-02-08T10:00:00-05:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-006',
    orgId:                      'acme',
    userId:                     '01HZP3CB6MKQ9X2TYACME006',
    email:                      'collab@vendor.test',
    role:                       'Member',
    status:                     'DEACTIVATED',
    displayName:                'Vendor Guest',
    joinedAt:                   '2026-03-21T10:00:00-05:00',
    invitationSentAt:           '2026-03-19T10:00:00-05:00',
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       '2026-03-21T10:00:00-05:00',
    invitedBy:                  '01HZP3CB6MKQ9X2TYACME001',
    createdAt:                  '2026-03-19T10:00:00-05:00',
    isActive:                   false,
  },

  // ── acme-eu tenant ────────────────────────────────────────────────
  {
    id:                         'om-acme-eu-001',
    orgId:                      'acme-eu',
    userId:                     '01HZP3CB6MKQ9X2TYACEU001',
    email:                      'niamh.obrien@acme-eu.test',
    role:                       'Admin',
    status:                     'ACTIVE',
    displayName:                "Niamh O'Brien",
    joinedAt:                   '2024-09-01T09:00:00+01:00',
    invitationSentAt:           null,
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       null,
    invitedBy:                  null,
    createdAt:                  '2024-09-01T09:00:00+01:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-eu-002',
    orgId:                      'acme-eu',
    userId:                     '01HZP3CB6MKQ9X2TYACEU002',
    email:                      'sean.walsh@acme-eu.test',
    role:                       'Department Head',
    status:                     'ACTIVE',
    displayName:                'Sean Walsh',
    joinedAt:                   '2024-10-14T09:00:00+01:00',
    invitationSentAt:           null,
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       null,
    invitedBy:                  null,
    createdAt:                  '2024-10-14T09:00:00+01:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-eu-003',
    orgId:                      'acme-eu',
    userId:                     '01HZP3CB6MKQ9X2TYACEU003',
    email:                      'aoife.murphy@acme-eu.test',
    role:                       'Member',
    status:                     'ACTIVE',
    displayName:                'Aoife Murphy',
    joinedAt:                   '2025-01-06T09:00:00+00:00',
    invitationSentAt:           null,
    invitationTokenExpiresAt:   null,
    invitationAcceptedAt:       null,
    invitedBy:                  null,
    createdAt:                  '2025-01-06T09:00:00+00:00',
    isActive:                   true,
  },
  {
    id:                         'om-acme-eu-004',
    orgId:                      'acme-eu',
    userId:                     null,
    email:                      'cillian.b@acme-eu.test',
    role:                       'Member',
    status:                     'INVITED',
    displayName:                'Cillian Byrne',
    joinedAt:                   null,
    invitationSentAt:           '2026-04-22T09:00:00+01:00',
    invitationTokenExpiresAt:   '2026-04-29T09:00:00+01:00',
    invitationAcceptedAt:       null,
    invitedBy:                  '01HZP3CB6MKQ9X2TYACEU001',
    createdAt:                  '2026-04-22T09:00:00+01:00',
    isActive:                   false,
  },
];

/**
 * Runtime store — mutable copy initialised from the seed set so that
 * mock POST /invite and DELETE handlers can splice rows in/out within
 * the same browser session. Only the mock-api module mutates this array.
 */
export const RUNTIME_ORG_MEMBERS: MockOrgMember[] = [...SEED_ORG_MEMBERS];
